import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Chip,
  Card,
  CardMedia,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { KBListItem } from "./KBListItem";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import CustomSnackbar from "../Common/snackBar";
import { getArticleByIdAction, getModulesAction, getTutorialUploadStatusAction, updateArticleFeedbackAction } from "../../Actions/KnowledgeBaseAction";
import { TutorialPlayerModal } from "./TutorialPlayerModal";
import { getPermissionData, getUserData } from "../../Actions/common";
import { MyEditOutlinedIcon } from "../../Assets/svg/MyIcons";
import "./Knowledgebase.css";

dayjs.extend(relativeTime);

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const KBArticleImageGallery = ({ images = [] }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  if (images.length === 0) return null;

  const gridCols =
    images.length === 1
      ? "1fr"
      : images.length === 2
        ? "repeat(2, 1fr)"
        : "repeat(3, 1fr)";

  return (
    <Box>
      <p className="kb-article-block-title">Images ({images.length})</p>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridCols,
          gap: 2,
        }}
      >
        {images.map((img, i) => (
          <Card
            key={i}
            sx={{
              overflow: "hidden",
              cursor: "pointer",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "grey.200",
              transition: "all 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: 4,
              },
            }}
            onClick={() => {
              try {
                window.open(img.url);
              } catch (_) {
                setSelectedImage(img);
              }
            }}
          >
            <CardMedia
              component="img"
              sx={{
                width: "100%",
                height: images.length === 1 ? 260 : 180,
                objectFit: "cover",
              }}
              image={img.url}
              alt={img.label || `Image ${i + 1}`}
            />
          </Card>
        ))}
      </Box>

      {selectedImage && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            bgcolor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
          }}
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage.url}
            alt={selectedImage.label}
            style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }}
          />
        </Box>
      )}
    </Box>
  );
};

const KBArticleAttachments = ({ attachments = [] }) => {
  const docs = attachments.filter((a) => {
    const t = (a.mediaType || "").toLowerCase();
    return !t.startsWith("video") && !t.startsWith("image");
  });
  if (docs.length === 0) return null;

  const docTypeIcon = (a) => {
    const label = (a.label || "").toLowerCase();
    const url = (a.url || "").toLowerCase();
    if (label.endsWith(".pdf") || url.endsWith(".pdf"))
      return <PictureAsPdfIcon sx={{ color: "#ef4444", fontSize: 20 }} />;
    return <DescriptionIcon sx={{ color: "#22419e", fontSize: 20 }} />;
  };

  return (
    <Box>
      <p className="kb-article-block-title">Documents ({docs.length})</p>
      <Stack direction="column" spacing={1.5}>
        {docs.map((doc, i) => (
          <div key={i} className="kb-doc-row">
            <div className="kb-doc-row__icon">
              {docTypeIcon(doc)}
            </div>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={doc.label || "Attachment"}
              >
                {doc.label || "Attachment"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {doc.size ? formatBytes(doc.size) : "Document"}
              </Typography>
            </Box>
            <Button
              startIcon={<DownloadIcon />}
              component="a"
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              color="primary"
              size="small"
            >
              Open
            </Button>
          </div>
        ))}
      </Stack>
    </Box>
  );
};

const KBVideoPlayerTrigger = ({ article, onOpen }) => {
  const videoAttachments = Array.isArray(article.attachments)
    ? article.attachments.filter((a) =>
      (a.mediaType || "").toLowerCase().startsWith("video"),
    )
    : [];

  if (videoAttachments.length === 0 && !article.videoUrl) return null;

  const firstVideo = videoAttachments[0];
  const thumbnailSrc = article.thumbnail || (firstVideo && firstVideo.thumbnail) || null;

  return (
    <Box>
      <p className="kb-article-block-title">Video Guide</p>
      <Box
        onClick={onOpen}
        sx={{
          cursor: "pointer",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          border: "1px solid",
          borderColor: "grey.200",
          bgcolor: "#0b1220",
          boxShadow: 2,
          transition: "all 0.25s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: 8,
            "& .play-button": {
              transform: "translate(-50%, -50%) scale(1.08)",
              bgcolor: "#22419e",
            },
          },
        }}
      >
        {thumbnailSrc ? (
          <Box sx={{ position: "relative" }}>
            <img
              src={thumbnailSrc}
              alt={article.title}
              style={{
                width: "100%",
                height: 320,
                objectFit: "cover",
                display: "block",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.38)",
              }}
            />
            <Box
              className="play-button"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                transition: "all 0.25s",
                bgcolor: "#22419e",
                color: "white",
                borderRadius: "50%",
                width: 72,
                height: 72,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: 4,
                border: "3px solid rgba(255,255,255,0.12)",
              }}
            >
              <PlayArrowIcon
                sx={{ fontSize: 36, ml: 0.5 }}
                htmlColor="white"
              />
            </Box>
            {article.duration && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  right: 14,
                  px: 1.2,
                  py: 0.4,
                  bgcolor: "rgba(0,0,0,0.75)",
                  borderRadius: 1,
                  color: "white",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {article.duration}
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <VideocamIcon
              sx={{ fontSize: 60, color: "#ffffff40" }}
              htmlColor="rgba(255,255,255,0.45)"
            />
            <Box
              className="play-button"
              sx={{
                bgcolor: "#22419e",
                color: "white",
                borderRadius: "50%",
                width: 64,
                height: 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: 3,
              }}
            >
              <PlayArrowIcon
                sx={{ fontSize: 34, ml: 0.5 }}
                htmlColor="white"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ArticleViewPage = () => {
  const { moduleSlug, articleId } = useParams();
  const navigate = useNavigate();

  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`)
    ? getPermissionData(`${userData?.id}_permissionData`)
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "knowledge-base",
  );

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [moduleMeta, setModuleMeta] = useState(null);

  useEffect(() => {
    getModulesAction().then((mods) => {
      const match = Array.isArray(mods) && mods.find((m) => m.slug === moduleSlug);
      setModuleMeta(match || null);
    });
  }, [moduleSlug]);

  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  useEffect(() => {
    (async () => {
      if (!articleId) return;
      setLoading(true);
      try {
        const res = await getArticleByIdAction(Number(articleId));

        setArticle(res);

        if (typeof res?.userFeedback === "boolean") {
          setFeedback(res.userFeedback ? "helpful" : "unhelpful");
        } else {
          setFeedback(null);
        }
        if (!res) {
          showSnack("Article not found or has been deleted.", "error");
          navigate(`/knowledge-base/${moduleSlug}`);
          return;
        }
        if (
          res.uploadStatus === "pending" ||
          res.uploadStatus === "processing"
        ) {
          showSnack(
            "Video is still uploading. View unlocks when upload reaches 100%.",
            "warning",
          );
          navigate(`/knowledge-base/${moduleSlug}`);
          return;
        }
        setArticle(res);
      } catch (err) {
        console.error(err);
        showSnack(err?.message || "Failed to load article", "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [articleId, moduleSlug]);

  const videoAttachments =
    article && Array.isArray(article.attachments)
      ? article.attachments.filter((a) => (a.mediaType || "").toLowerCase().startsWith("video"))
      : [];
  const imageAttachments =
    article && Array.isArray(article.attachments)
      ? article.attachments.filter((a) => (a.mediaType || "").toLowerCase().startsWith("image"))
      : [];

  const handleFeedback = async (value) => {
    try {
      const helpful = value === "helpful";

      await updateArticleFeedbackAction({
        tutorialId: article.id,
        helpful,
      });

      setFeedback(value);
    } catch (error) {
      console.error("Failed to update article feedback:", error);
    }
  };

  if (loading && !article) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 12 }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only"></span>
        </div>
      </Box>
    );
  }

  if (!article) return null;

  const articleAttachments = article.attachments || [];
  const moreArticles = article.moreArticles || [];
  const prevArticle = article.prevArticle || null;
  const nextArticle = article.nextArticle || null;
  const moduleName = article.moduleName || (moduleMeta ? moduleMeta.moduleName : moduleSlug);
  const helpfulCount = Number(article.totalHelpful) || 0;
  const unhelpfulCount = Number(article.totalUnhelpful) || 0;

  return (
    <div id="kt_content_container" className="kb-article-page">
      <BreadcrumbsComponent
        breadcrumbs={[
          { label: "Knowledge Base", path: "/knowledge-base" },
          {
            label: moduleName || moduleSlug,
            path: `/knowledge-base/${moduleSlug}`,
          },
          { label: "Article View" },
        ]}
      />

      <div className="kb-page-head">
        <div className="kb-page-head-main">
          <Tooltip title="Back" placement="top" arrow>
            <IconButton
              className="kb-back-icon"
              onClick={() => navigate(`/knowledge-base/${moduleSlug}`)}
              aria-label="Back"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <div>
            <Chip className="kb-chip" label={moduleName} size="small" />
            <h1>{article.title}</h1>
          </div>
        </div>
        {hasPermission?.canEdit && (
          <Button
            variant="contained"
            sx={{ bgcolor: "#22419e", fontWeight: 700, textTransform: "none" }}
            startIcon={<MyEditOutlinedIcon />}
            onClick={() => navigate(`/knowledge-base/${moduleSlug}/${articleId}/edit`)}
          >
            Edit
          </Button>
        )}
      </div>

      <div className="kb-article-meta">
        <span>
          <strong>Last updated:</strong>{" "}
          {dayjs(article.updatedAt).format("DD MMM YYYY, HH:mm")}
        </span>
        <span className="kb-meta-dot" />
        <span>{article.totalViews || 0} views</span>
        <span className="kb-meta-dot" />
        <span className="kb-meta-stats">
          <span className="kb-meta-stat is-up">
            <ThumbUpAltIcon />
            {helpfulCount}
          </span>
          <span className="kb-meta-stat is-down">
            <ThumbDownAltIcon />
            {unhelpfulCount}
          </span>
        </span>
      </div>

      <Card
        elevation={0}
        className="kb-article-card"
      >
        <Stack direction="column" spacing={4}>
          {videoAttachments.length > 0 || article.videoUrl ? (
            <KBVideoPlayerTrigger
              article={article}
              onOpen={() => setPlayerOpen(true)}
            />
          ) : null}

          {article.shortDescription && (
            <Box>
              <p className="kb-article-block-title">Summary</p>
              <Typography
                variant="body1"
                sx={{
                  color: "#475569",
                  fontSize: 15,
                  lineHeight: 1.7,
                }}
              >
                {article.shortDescription}
              </Typography>
            </Box>
          )}

          {article.content && (
            <Box>
              <p className="kb-article-block-title">Content</p>
              <Box
                sx={{
                  lineHeight: 1.8,
                  fontSize: 15,
                  color: "#1F2937",
                  "& img": { maxWidth: "100%", borderRadius: 2, my: 2 },
                  "& a": { color: "#22419e", textDecoration: "underline" },
                  "& h1": { fontSize: "1.8rem", fontWeight: 800, mt: 3, mb: 1 },
                  "& h2": { fontSize: "1.5rem", fontWeight: 800, mt: 3, mb: 1 },
                  "& h3": { fontSize: "1.25rem", fontWeight: 700, mt: 2, mb: 1 },
                  "& ul, ol": { pl: 4 },
                  "& p": { mb: 1.5 },
                  "& code": {
                    bgcolor: "#f3f4f6",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: 14,
                  },
                  "& pre": {
                    bgcolor: "#0b1220",
                    color: "white",
                    p: 2.5,
                    borderRadius: 2,
                    overflowX: "auto",
                  },
                  "& table": { width: "100%", borderCollapse: "collapse" },
                  "& th, & td": {
                    border: "1px solid #e5e7eb",
                    p: 1,
                    fontSize: 14,
                  },
                  "& th": { bgcolor: "#f9fafb" },
                }}
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </Box>
          )}

          {imageAttachments.length > 0 ? (
            <Divider sx={{ my: 1 }} />
          ) : null}

          <KBArticleImageGallery images={imageAttachments} />

          {imageAttachments.length > 0 ? (
            <Divider sx={{ my: 1 }} />
          ) : null}

          <KBArticleAttachments attachments={articleAttachments} />

          {articleAttachments.length > 0 && imageAttachments.length === 0
            ? <Divider sx={{ my: 1 }} />
            : null}

          {/* ENGAGEMENT */}
          <Box>
            <p className="kb-article-block-title">Was this knowledge base helpful?</p>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <ToggleButtonGroup
                className="kb-feedback-group"
                value={feedback}
                exclusive
                onChange={(_, v) => { if (v !== null) handleFeedback(v); }}
                aria-label="helpful feedback"
              >
                <ToggleButton
                  value="helpful"
                  sx={{
                    py: 1.3,
                    px: 2.5,
                    textTransform: "none",
                    fontWeight: 700,
                    "&.Mui-selected, &.Mui-selected:hover": {
                      bgcolor: "#22c55e !important",
                      color: "white !important",
                      borderColor: "#16a34a !important",
                    },
                    "&:not(.Mui-selected):hover": {
                      bgcolor: "rgba(34,197,94,0.08)",
                    },
                    "& svg": { mr: 0.5 },
                  }}
                >
                  <ThumbUpOffAltIcon fontSize="small" /> Helpful
                </ToggleButton>
                <ToggleButton
                  value="unhelpful"
                  sx={{
                    py: 1.3,
                    px: 2.5,
                    textTransform: "none",
                    fontWeight: 700,
                    "&.Mui-selected, &.Mui-selected:hover": {
                      bgcolor: "#ef4444 !important",
                      color: "white !important",
                      borderColor: "#dc2626 !important",
                    },
                    "&:not(.Mui-selected):hover": {
                      bgcolor: "rgba(239,68,68,0.08)",
                    },
                    "& svg": { mr: 0.5 },
                  }}
                >
                  <ThumbDownOffAltIcon fontSize="small" /> Not Helpful
                </ToggleButton>
              </ToggleButtonGroup>

              <Box
                sx={{
                  ml: { xs: 0, sm: 2 },
                  color: "#6B7280",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                <Box component="span" sx={{ color: "#22c55e", fontWeight: 800 }}>
                  {helpfulCount}
                </Box>{" "}
                found helpful
              </Box>
            </Box>
          </Box>

          {/* Prev / Next */}
          {(prevArticle || nextArticle) && (
            <div className="kb-nav-pair">
              <Button
                startIcon={<NavigateBeforeIcon />}
                variant="outlined"
                disabled={!prevArticle}
                onClick={() =>
                  prevArticle &&
                  navigate(`/knowledge-base/${prevArticle.moduleSlug || moduleSlug}/${prevArticle.id}`)
                }
                sx={{
                  fontWeight: 700,
                  minWidth: 180,
                  justifyContent: "flex-start",
                  textTransform: "none",
                  p: 1.5,
                  borderRadius: 2,
                  "& .MuiButton-startIcon": { mr: 1 },
                }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <Box sx={{ fontSize: 10, fontWeight: 600, opacity: 0.6, lineHeight: 1, mb: 0.25 }}>
                    PREVIOUS
                  </Box>
                  <Box sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                    {prevArticle ? prevArticle.title : "No previous article"}
                  </Box>
                </Box>
              </Button>
              <Button
                endIcon={<NavigateNextIcon />}
                variant="outlined"
                disabled={!nextArticle}
                onClick={() =>
                  nextArticle &&
                  navigate(`/knowledge-base/${nextArticle.moduleSlug || moduleSlug}/${nextArticle.id}`)
                }
                sx={{
                  fontWeight: 700,
                  minWidth: 180,
                  justifyContent: "flex-end",
                  textTransform: "none",
                  p: 1.5,
                  borderRadius: 2,
                  ml: "auto",
                }}
              >
                <Box sx={{ textAlign: "right" }}>
                  <Box sx={{ fontSize: 10, fontWeight: 600, opacity: 0.6, lineHeight: 1, mb: 0.25 }}>
                    NEXT
                  </Box>
                  <Box sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                    {nextArticle ? nextArticle.title : "No next article"}
                  </Box>
                </Box>
              </Button>
            </div>
          )}
        </Stack>
      </Card>

      {moreArticles.length > 0 && (
        <section className="kb-section">
          <div className="kb-section-head">
            <h2>More Articles</h2>
          </div>
          <div className="kb-results-grid">
            {moreArticles.map((a) => (
              <KBListItem
                key={a.id}
                article={a}
                onClick={() =>
                  navigate(`/knowledge-base/${a.moduleSlug || moduleSlug}/${a.id}`)
                }
              />
            ))}
          </div>
        </section>
      )}

      <TutorialPlayerModal
        open={playerOpen}
        onClose={() => setPlayerOpen(false)}
        tutorial={article}
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      />
    </div>
  );
};

export default ArticleViewPage;